import type { TaxForm } from '../types/index';

// ============================================================================
// INTEGER-CENT MATH (System_Foundation.md)
// ============================================================================

export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

export function centsToDollars(cents: number): number {
  return cents / 100;
}

/**
 * Format currency WITHOUT $ symbol
 * Per Sprint 2.5 requirement: Remove $ symbol, keep comma separators
 */
export function formatCurrency(cents: number): string {
  const dollars = centsToDollars(cents);
  // Use 'decimal' style instead of 'currency' to avoid $ symbol
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format currency rounded to whole dollars WITHOUT $ symbol
 * Per Sprint 2.5 requirement: Remove $ symbol, keep comma separators
 */
export function formatCurrencyRounded(cents: number): string {
  const dollars = centsToDollars(cents);
  const rounded = Math.round(dollars);
  // Use 'decimal' style instead of 'currency' to avoid $ symbol
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
}

/**
 * Display amount with proper formatting
 * - Zero values display as dash (-)
 * - Negative values in parentheses
 * - No $ symbol per requirement
 */
export function displayAmount(cents: number): { text: string; isNegative: boolean; isZero: boolean } {
  if (cents === 0) {
    return { text: '-', isNegative: false, isZero: true };
  }
  const isNegative = cents < 0;
  const formatted = formatCurrency(Math.abs(cents));
  // Parentheses for negative values
  const text = isNegative ? `(${formatted})` : formatted;
  return { text, isNegative, isZero: false };
}

/**
 * Display amount rounded to whole dollars with proper formatting
 * - Zero values display as dash (-)
 * - Negative values in parentheses
 * - No $ symbol per requirement
 */
export function displayAmountRounded(cents: number): { text: string; isNegative: boolean; isZero: boolean } {
  if (cents === 0) {
    return { text: '-', isNegative: false, isZero: true };
  }
  const isNegative = cents < 0;
  const formatted = formatCurrencyRounded(Math.abs(cents));
  // Parentheses for negative values
  const text = isNegative ? `(${formatted})` : formatted;
  return { text, isNegative, isZero: false };
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

export function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export function parseDate(input: string): string | null {
  const cleaned = input.replace(/\D/g, '');
  if (cleaned.length === 8) {
    const month = cleaned.substring(0, 2);
    const day = cleaned.substring(2, 4);
    const year = cleaned.substring(4, 8);
    const date = new Date(`${year}-${month}-${day}`);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }
  const date = new Date(input);
  if (!isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }
  return null;
}

export function autoFormatDateInput(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  }
  return value;
}

// ============================================================================
// FILE & FORM CONSTANTS
// ============================================================================

export const TAX_FORMS: TaxForm[] = ['1120', '1120-S', '1065', '1040-Sch-C'];

export function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, '_');
}

export function generateFilename(clientName: string, taxYearEnd: string, taxForm: string): string {
  const year = new Date(taxYearEnd).getFullYear();
  return `${sanitizeFilename(clientName)}_${year}_${sanitizeFilename(taxForm)}.TAXTRAIL`;
}

export function exportToFile(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
}

// ============================================================================
// VALIDATION (Restored from Original Implementation)
// ============================================================================

export function validateRequired(value: string | undefined): string | null {
  if (!value?.trim()) {
    return 'Required';
  }
  return null;
}

export function validateDate(value: string | undefined): string | null {
  if (!value) {
    return 'Enter a valid date (MM/DD/YYYY)';
  }
  const parsed = parseDate(value);
  if (!parsed) {
    return 'Enter a valid date (MM/DD/YYYY)';
  }
  return null;
}

export function validateTaxForm(value: string | undefined): string | null {
  if (!value) {
    return 'Select a form';
  }
  if (!TAX_FORMS.includes(value as TaxForm)) {
    return 'Invalid tax form';
  }
  return null;
}
